import React, { useMemo, useState } from "react";

/* ------------------------------------------------------------------ *
 * Full-screen popup that shows every contact inside a list.
 *  - toggle (enable / disable) any contact so it is skipped for this
 *    campaign (disabled ids are pushed into excludedContacts)
 *  - Select all / Deselect all
 *  - search within the list
 * ------------------------------------------------------------------ */
export default function ListContactsModal({
  list,
  listContacts,
  excludedIds,
  onToggleContact,
  onEnableAll,
  onDisableAll,
  onClose
}) {
  const [q, setQ] = useState("");

  const excludedSet = useMemo(() => new Set(excludedIds || []), [excludedIds]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return listContacts;
    return listContacts.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.company || "").toLowerCase().includes(s)
    );
  }, [listContacts, q]);

  const enabledCount = listContacts.filter((c) => !excludedSet.has(c._id)).length;

  function initials(c) {
    const base = (c.name || c.email || "?").trim();
    const parts = base.split(/\s+/);
    if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 2).toUpperCase();
  }

  return (
    <div className="lcm-overlay" onClick={onClose}>
      <div className="lcm-shell" onClick={(e) => e.stopPropagation()}>
        <header className="lcm-head">
          <div className="lcm-head-info">
            <span className="lcm-ic" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M16 19c0-2.2-2.7-4-6-4s-6 1.8-6 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                <circle cx="10" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7"/>
                <path d="M20 18c0-1.6-1.3-2.9-3-3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                <path d="M16.5 5.2A3 3 0 0 1 17 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
              </svg>
            </span>
            <div>
              <h2>{list.name}</h2>
              <p>
                <b>{enabledCount}</b> of {listContacts.length} contacts will receive this campaign
              </p>
            </div>
          </div>
          <button type="button" className="lcm-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="lcm-toolbar">
          <div className="lcm-search">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search contacts in this list..."
            />
          </div>
          <div className="lcm-bulk">
            <button type="button" onClick={onEnableAll}>Enable all</button>
            <button type="button" onClick={onDisableAll}>Disable all</button>
          </div>
        </div>

        <div className="lcm-body">
          {rows.length === 0 ? (
            <p className="lcm-empty">No contacts found in this list.</p>
          ) : (
            <div className="lcm-grid">
              {rows.map((c) => {
                const disabled = excludedSet.has(c._id);
                return (
                  <div
                    key={c._id}
                    className={`lcm-card ${disabled ? "is-off" : "is-on"}`}
                    onClick={() => onToggleContact(c._id)}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="lcm-avatar">{initials(c)}</span>
                    <div className="lcm-meta">
                      <b>{c.name || c.email}</b>
                      <span>{c.email}</span>
                    </div>
                    <span className={`lcm-status ${disabled ? "off" : "on"}`}>
                      {disabled ? "Skipped" : "Will send"}
                    </span>
                    <span className={`lcm-switch ${disabled ? "" : "on"}`} aria-hidden="true">
                      <i />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <footer className="lcm-foot">
          <span>
            <b>{enabledCount}</b> selected · {listContacts.length - enabledCount} skipped
          </span>
          <button type="button" className="lcm-done" onClick={onClose}>
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
